import { inject, Injectable } from '@angular/core';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { RolloutProgressReportDefinitionsService } from '../definitions/rollout-progress-report-definitions.service';
import { RolloutProgressReportEntities } from '../entities';
import { RolloutProgressReportGroupsService } from '../groups/rollout-progress-report-groups.service';
import { InspectionReportInitService } from '../initial-values/inspection-report-init.service';
import { RolloutProgressReportsService } from '../reports/rollout-progress-reports.service';

@Injectable({
  providedIn: 'root',
})
export class RolloutProgressReportCombinedService {
  private readonly reportService = inject(RolloutProgressReportsService);
  private readonly groupsService = inject(RolloutProgressReportGroupsService);
  private readonly definitionService = inject(
    RolloutProgressReportDefinitionsService,
  );
  private readonly initService = inject(InspectionReportInitService);

  //v2
  getInspectionGroupsAndReportData$ =
    (): Observable<RolloutProgressReportEntities.v3.InspectionGroupsAndReportLoadingState> =>
      this.reportService
        .getReport$(
          this.initService.getInitValueProperty('ifs'),
          this.initService.getInitValueProperty('typeId'),
          this.initService.getInitValueProperty('markerId'),
        )
        .pipe(
          switchMap((report) => this.getInspectionReportData$(report)),
          map(
            ([reportState, inspectionGroupsState]) =>
              ({
                reportState,
                inspectionGroupsState,
              }) as RolloutProgressReportEntities.v3.InspectionGroupsAndReportLoadingState,
          ),
        );

  getInspectionReportData$ = (
    report: RolloutProgressReportEntities.v3.ReportQueryLoadingState,
  ): Observable<
    [
      RolloutProgressReportEntities.v3.ReportQueryLoadingState,
      RolloutProgressReportEntities.v3.InspectionReportGroupsDataQueryLoadingState | null,
    ]
  > =>
    report.data
      ? combineLatest([
          of(report),
          this.groupsService.getInspectionReportGroupsData$(
            this.reportService.getStoredReportId(),
            this.initService.getInitValueProperty('typeId'),
          ),
        ])
      : of([report, null]);

  //v1
  getDefinitionsAndReportsAndGroups$ = (
    ifs: string,
    pointType: RolloutProgressReportEntities.v3.MarkerTypes,
    markerId: string,
  ): Observable<RolloutProgressReportEntities.v3.QueryLoadingStates> =>
    combineLatest([
      this.definitionService.getDefinitions$(pointType),
      this.reportService
        .getReport$(ifs, pointType, markerId)
        .pipe(switchMap((report) => this.combineReportAndGroups$(report))),
    ]).pipe(
      map(
        ([
          definitionState,
          [reportState, reportGroupsState],
        ]: RolloutProgressReportEntities.v3.CombinedQueryLoadingState) => ({
          definitionState,
          reportState,
          reportGroupsState,
        }),
      ),
    );

  combineReportAndGroups$ = (
    report: RolloutProgressReportEntities.v3.ReportQueryLoadingState,
  ): Observable<
    [
      RolloutProgressReportEntities.v3.ReportQueryLoadingState,
      RolloutProgressReportEntities.v3.ReportGroupsQueryLoadingState | null,
    ]
  > =>
    report.data
      ? combineLatest([
          of(report),
          this.groupsService.getReportGroups$(report.data.id),
        ])
      : of([report, null]);
}
